import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import i18n from "@/i18n";
import { Bell, Check, AtSign } from "lucide-react";

interface Notif {
  id: string;
  type: string;
  title: string;
  body?: string | null;
  link?: string | null;
  read: boolean;
  createdAt: string;
}

interface NotifResponse {
  data: Notif[];
  unread: number;
  success: true;
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return i18n.t("shared.notifications.justNow");
  if (m < 60) return i18n.t("shared.notifications.minutesShort", { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return i18n.t("shared.notifications.hoursShort", { count: h });
  return i18n.t("shared.notifications.daysShort", { count: Math.floor(h / 24) });
}

/** Header bell — in-app notifications (vision §5.15). Polls every 60s; click opens the linked record. */
export default function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<NotifResponse>("/api/admin/notifications"),
    refetchInterval: 60000,
  });

  const notifs = data?.data ?? [];
  const unread = data?.unread ?? 0;

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const markAll = useMutation({
    mutationFn: () => api.post("/api/admin/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const openNotif = (n: Notif) => {
    if (!n.read) markRead.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
        aria-label={t("shared.notifications.title")}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="bg-accent-500 absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2 dark:border-gray-700">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {t("shared.notifications.title")}
              </span>
              {unread > 0 && (
                <button
                  onClick={() => markAll.mutate()}
                  className="text-primary-600 dark:text-primary-400 flex items-center gap-1 text-xs hover:underline"
                >
                  <Check className="h-3 w-3" /> {t("shared.notifications.markAllRead")}
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {notifs.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  {t("shared.notifications.empty")}
                </div>
              ) : (
                notifs.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => openNotif(n)}
                    className={`flex w-full gap-3 border-b border-gray-100 px-4 py-3 text-left transition-colors last:border-0 hover:bg-gray-50 dark:border-gray-700/60 dark:hover:bg-gray-700/40 ${
                      n.read ? "" : "bg-primary-50/50 dark:bg-primary-900/10"
                    }`}
                  >
                    <div className="bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                      <AtSign className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                          {n.title}
                        </p>
                        <span className="shrink-0 text-xs text-gray-400">
                          {relTime(n.createdAt)}
                        </span>
                      </div>
                      {n.body && (
                        <p className="mt-0.5 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                          {n.body}
                        </p>
                      )}
                    </div>
                    {!n.read && (
                      <span className="bg-accent-500 mt-1 h-2 w-2 shrink-0 rounded-full" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
